import { useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Chip-based multi-email editor. Type an address, press Enter / comma /
// space / Tab to commit. Click X to remove. Paste a comma-separated
// list to bulk-add. Stored as a string[] of lowercase, trimmed,
// de-duplicated emails.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EmailListInput({ value = [], onChange, label, placeholder = 'name@example.com', helpText }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  const list = Array.isArray(value) ? value : [];

  function commit(raw) {
    const candidates = String(raw || '')
      .split(/[,\s;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    if (candidates.length === 0) return;

    const valid = [];
    const invalid = [];
    for (const c of candidates) {
      if (EMAIL_RE.test(c)) valid.push(c);
      else invalid.push(c);
    }
    if (invalid.length > 0) {
      setError(`Not a valid email: ${invalid.join(', ')}`);
    } else {
      setError('');
    }
    if (valid.length > 0) {
      const merged = [...new Set([...list, ...valid])];
      onChange(merged);
    }
    setDraft('');
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
      if (draft.trim()) {
        e.preventDefault();
        commit(draft);
      }
    } else if (e.key === 'Backspace' && draft === '' && list.length > 0) {
      // Pop the last chip.
      onChange(list.slice(0, -1));
    }
  }

  function handlePaste(e) {
    const text = e.clipboardData.getData('text');
    if (text && /[,\s;]/.test(text)) {
      e.preventDefault();
      commit(text);
    }
  }

  function remove(email) {
    onChange(list.filter((e) => e !== email));
  }

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-[13px] font-medium text-muted-foreground">{label}</label>
      )}
      <div
        className={cn(
          'flex min-h-10 flex-wrap items-center gap-1.5 rounded-lg border bg-card px-2 py-1.5 shadow-sm focus-within:ring-2 focus-within:ring-ring/30 focus-within:border-primary/50',
          error ? 'border-destructive/50' : 'border-border'
        )}
      >
        {list.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
          >
            {email}
            <button
              type="button"
              onClick={() => remove(email)}
              className="rounded p-0.5 text-primary/70 hover:bg-primary/20 hover:text-primary"
              title={`Remove ${email}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          type="email"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setError(''); }}
          onKeyDown={handleKeyDown}
          onBlur={() => draft.trim() && commit(draft)}
          onPaste={handlePaste}
          placeholder={list.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[160px] bg-transparent px-1.5 py-1 text-sm text-foreground outline-none placeholder:text-muted-foreground/60"
        />
      </div>
      {(helpText || error) && (
        <span className={cn('text-xs', error ? 'text-destructive' : 'text-muted-foreground')}>
          {error || helpText}
        </span>
      )}
    </div>
  );
}
